import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, RotateCcw, SendHorizonal, ArrowLeft, Share2, Loader2 } from "lucide-react";
import CrystalBall from "./CrystalBall";
import { playSpeech } from "@/audio";
import { buildShareCard, shareOrDownload } from "@/shareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const titleLine = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

const OracleHero = ({ t, voiceOn }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("prompt");
  const [mode, setMode] = useState("libre");
  const [sessionId, setSessionId] = useState(null);
  const [question, setQuestion] = useState("");
  const [askedQuestion, setAskedQuestion] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareMsg, setShareMsg] = useState("");
  const [oracleMsg, setOracleMsg] = useState("");
  const [clarifyCount, setClarifyCount] = useState(0);
  const [answer, setAnswer] = useState("");
  const [verdict, setVerdict] = useState(null);
  const [image, setImage] = useState(null);
  const [streamDone, setStreamDone] = useState(false);
  const [invalidMsg, setInvalidMsg] = useState("");
  const [shakeKey, setShakeKey] = useState(0);
  const [loadingLine, setLoadingLine] = useState(0);
  const inputRef = useRef(null);
  const backPhaseRef = useRef("prompt");
  const answerEndRef = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const ballX = useTransform(sx, [-1, 1], [-10, 10]);
  const ballY = useTransform(sy, [-1, 1], [-8, 8]);

  useEffect(() => {
    if (phase !== "revealed" || !answerEndRef.current) return;
    const rect = answerEndRef.current.getBoundingClientRect();
    if (rect.bottom > window.innerHeight - 24) {
      answerEndRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [phase, answer, streamDone, verdict]);

  useEffect(() => {
    if (phase !== "loading") return;
    const id = setInterval(() => setLoadingLine((l) => (l + 1) % t.loadingLines.length), 2600);
    return () => clearInterval(id);
  }, [phase, t.loadingLines.length]);

  const onMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 2);
    my.set((e.clientY / window.innerHeight - 0.5) * 2);
  };

  const fail = (msg) => {
    setInvalidMsg(msg);
    setShakeKey((k) => k + 1);
    setPhase(backPhaseRef.current);
  };

  const speak = async (text) => {
    if (!voiceOn || !text) return;
    try {
      const { data } = await axios.post(`${API}/oracle/speak`, { text }, { timeout: 90000 });
      if (data.url) await playSpeech(`${BACKEND_URL}${data.url}`);
    } catch {
      /* la voix se tait dans les brumes */
    }
  };

  const ask = async (e) => {
    e?.preventDefault();
    const q = question.trim();
    const minLen = backPhaseRef.current === "clarify" || phase === "clarify" ? 1 : 8;
    if (q.length < minLen || !/[a-zA-ZÀ-ÿ]/i.test(q)) {
      fail(t.tooShort);
      return;
    }
    backPhaseRef.current = phase === "loading" ? backPhaseRef.current : phase;
    if (!sessionId) setAskedQuestion(q);
    setInvalidMsg("");
    setPhase("loading");
    setStreamDone(false);
    let gotAnswer = false;
    let streamed = "";

    const handle = (ev) => {
      if (ev.session_id) setSessionId(ev.session_id);
      if (ev.type === "start") {
        setAnswer("");
        setVerdict(null);
        setImage(null);
        setPhase("revealed");
      } else if (ev.type === "token") {
        streamed += ev.text;
        setAnswer(streamed);
      } else if (ev.type === "replace") {
        streamed = ev.text;
        setAnswer(streamed);
      } else if (ev.type === "clarify") {
        setOracleMsg(ev.message);
        setClarifyCount(ev.clarify_count);
        setQuestion("");
        setPhase("clarify");
        speak(ev.message);
        setTimeout(() => inputRef.current?.focus(), 500);
      } else if (ev.type === "answer") {
        gotAnswer = true;
        setAnswer(ev.answer);
        setVerdict(ev.verdict || null);
        setPhase("revealed");
        speak(ev.answer);
      } else if (ev.type === "image") {
        setImage(ev.image_base64 ? `data:image/png;base64,${ev.image_base64}` : null);
        setStreamDone(true);
      } else if (ev.type === "error") {
        throw new Error("stream");
      }
    };

    try {
      const res = await fetch(`${API}/oracle/ask/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, session_id: sessionId, lang: t.lang, mode }),
      });
      if (!res.ok || !res.body) throw new Error("http");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          for (const line of chunk.split("\n")) {
            if (line.startsWith("data: ")) handle(JSON.parse(line.slice(6)));
          }
        }
      }
      if (gotAnswer) setStreamDone(true);
    } catch {
      if (gotAnswer) setStreamDone(true);
      else fail(t.errorGeneric);
    }
  };

  const share = async () => {
    if (sharing) return;
    setSharing(true);
    setShareMsg("");
    try {
      const blob = await buildShareCard({
        brand: `${t.titleA} ${t.titleB}`,
        question: askedQuestion,
        verdict,
        answer,
        image,
        footer: t.footer,
      });
      const result = await shareOrDownload(blob, "oracle-de-cristal.png", `${t.titleA} ${t.titleB}`);
      setShareMsg(result === "shared" ? t.shareDoneShared : t.shareDoneDownloaded);
    } catch {
      setShareMsg(t.shareError);
    } finally {
      setSharing(false);
    }
  };

  const reset = () => {
    setPhase("prompt");
    backPhaseRef.current = "prompt";
    setSessionId(null);
    setQuestion("");
    setAskedQuestion("");
    setShareMsg("");
    setOracleMsg("");
    setClarifyCount(0);
    setAnswer("");
    setVerdict(null);
    setImage(null);
    setStreamDone(false);
    setInvalidMsg("");
    navigate("/");
  };

  const words = answer.split(" ");
  const ballPhase =
    phase === "loading" ? "loading" : phase === "revealed" ? (image || streamDone ? "revealed" : "loading") : "prompt";
  const isClarify = phase === "clarify";

  return (
    <section
      data-testid="oracle-hero"
      onMouseMove={onMouseMove}
      className="relative z-10 flex min-h-[100svh] flex-col items-center justify-center px-6 pb-20 pt-24"
    >
      <motion.button
        type="button"
        onClick={() => navigate("/")}
        data-testid="back-home-link"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
        className="group fixed left-5 top-5 z-50 flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl transition-[color,border-color] duration-300 hover:border-amber-500/40 hover:text-amber-300"
      >
        <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
        {t.back}
      </motion.button>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.15 }}
        className="mb-6 flex flex-wrap items-center justify-center gap-3 text-center text-xs font-medium uppercase tracking-[0.45em] text-amber-500/80"
      >
        <Sparkles className="h-4 w-4" strokeWidth={1.5} />
        {t.eyebrow}
      </motion.p>

      <h1 className="mb-10 text-center font-serif font-light leading-[1.02] tracking-tight text-white/95">
        <span className="block overflow-hidden">
          <motion.span
            className="block text-4xl sm:text-5xl lg:text-6xl"
            variants={titleLine}
            custom={0}
            initial="hidden"
            animate="visible"
          >
            {t.titleA}
          </motion.span>
        </span>
        <span className="block overflow-hidden">
          <motion.span
            className="block text-4xl italic text-amber-400 sm:text-5xl lg:text-6xl"
            variants={titleLine}
            custom={1}
            initial="hidden"
            animate="visible"
          >
            {t.titleB}
          </motion.span>
        </span>
      </h1>

      <motion.div style={{ x: ballX, y: ballY }}>
        <CrystalBall
          phase={ballPhase}
          revealedImage={image}
          shakeKey={shakeKey}
          size={phase === "revealed" ? "w-[min(48vw,200px)]" : "w-[min(60vw,300px)]"}
        />
      </motion.div>

      <div className="mt-10 flex min-h-[210px] w-full max-w-3xl flex-col items-center">
        <AnimatePresence mode="wait">
          {(phase === "prompt" || phase === "clarify") && (
            <motion.form
              key={phase}
              data-testid="question-form"
              onSubmit={ask}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.7 }}
              className="w-full"
            >
              {isClarify ? (
                <div className="mb-6 text-center">
                  <p className="mb-2 flex items-center justify-center gap-3 text-base font-medium uppercase tracking-[0.3em] text-amber-500/80">
                    {t.clarifyLabel}
                    <span className="flex gap-1.5" data-testid="clarify-counter">
                      {[0, 1, 2].map((d) => (
                        <span
                          key={d}
                          className={`h-2 w-2 rounded-full ${d < clarifyCount ? "bg-amber-400" : "bg-white/15"}`}
                        />
                      ))}
                    </span>
                  </p>
                  <p
                    data-testid="clarify-question"
                    className="font-serif text-3xl font-light italic leading-snug text-white/95 sm:text-4xl"
                    style={{ textShadow: "0 2px 14px rgba(3,0,20,0.9)" }}
                  >
                    « {oracleMsg} »
                  </p>
                </div>
              ) : (
                <>
                  <p className="mb-5 text-center font-serif text-2xl font-light italic text-white/85 sm:text-3xl">
                    {t.invite}
                  </p>
                  <div data-testid="mode-toggle" className="mb-5 flex justify-center gap-2">
                    {[
                      { id: "libre", label: t.modeFree },
                      { id: "oui_non", label: t.modeYesNo },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`mode-${m.id}`}
                        onClick={() => setMode(m.id)}
                        aria-pressed={mode === m.id}
                        className={`rounded-full border px-6 py-2.5 text-base font-medium tracking-wide transition-[background-color,border-color,color] duration-300 ${
                          mode === m.id
                            ? "border-amber-500/60 bg-amber-500/15 text-amber-300"
                            : "border-white/10 bg-black/30 text-white/60 hover:border-white/25 hover:text-white/90"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="flex items-center gap-2 rounded-full border border-white/30 bg-black/60 p-2.5 pl-7 shadow-[0_0_40px_rgba(245,158,11,0.15),0_8px_32px_rgba(139,92,246,0.25)] backdrop-blur-2xl transition-[border-color,box-shadow] duration-300 focus-within:border-amber-400/70 focus-within:shadow-[0_0_50px_rgba(245,158,11,0.3),0_8px_32px_rgba(139,92,246,0.3)]">
                <input
                  ref={inputRef}
                  data-testid="question-input"
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={isClarify ? t.clarifyPlaceholder : t.placeholder}
                  maxLength={500}
                  className="oracle-input w-full rounded-full bg-transparent text-xl font-medium text-white placeholder:text-slate-300 sm:text-2xl"
                />
                <button
                  type="submit"
                  data-testid="ask-submit-button"
                  className="group flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[#030014] transition-[background-color,transform] duration-300 hover:bg-amber-300 active:scale-95"
                  aria-label={t.sendAria}
                >
                  <SendHorizonal className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </div>
              <AnimatePresence>
                {invalidMsg && (
                  <motion.p
                    data-testid="error-hint"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 text-center text-lg text-amber-300"
                  >
                    {invalidMsg}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.form>
          )}

          {phase === "loading" && (
            <motion.div
              key="loading"
              data-testid="loading-indicator"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center gap-5"
            >
              <div className="h-px w-24 overflow-hidden bg-white/10">
                <motion.div
                  className="h-full w-1/3 bg-amber-400/80"
                  animate={{ x: ["-100%", "300%"] }}
                  transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                />
              </div>
              <AnimatePresence mode="wait">
                <motion.p
                  key={loadingLine}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.6 }}
                  className="text-center font-serif text-2xl font-light italic text-white/85 sm:text-3xl"
                >
                  {t.loadingLines[loadingLine]}
                </motion.p>
              </AnimatePresence>
            </motion.div>
          )}

          {phase === "revealed" && (
            <motion.div
              key="revealed"
              data-testid="revelation-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9 }}
              className="w-full"
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="mb-8 text-center"
              >
                <p className="mb-2 text-sm font-medium uppercase tracking-[0.35em] text-[#D4A017]/80">{t.yourQuestion}</p>
                <p
                  data-testid="asked-question-text"
                  className="mx-auto max-w-2xl font-serif text-2xl font-semibold italic leading-snug text-[#D4A017] sm:text-3xl"
                  style={{ textShadow: "0 0 24px rgba(212,160,23,0.3), 0 2px 14px rgba(3,0,20,0.9)" }}
                >
                  « {askedQuestion} »
                </p>
              </motion.div>
              {verdict && (
                <motion.p
                  data-testid="verdict-text"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="mb-6 text-center font-serif text-6xl font-light tracking-wide text-amber-400 sm:text-7xl"
                  style={{ textShadow: "0 0 40px rgba(245,158,11,0.45)" }}
                >
                  {verdict}
                </motion.p>
              )}
              <p
                data-testid="oracle-answer-text"
                aria-live="polite"
                className="mx-auto max-w-3xl text-justify font-serif text-2xl font-light leading-relaxed text-white/95 sm:text-3xl lg:text-4xl"
                style={{ textShadow: "0 2px 14px rgba(3,0,20,0.95)", textAlignLast: "left", hyphens: "auto" }}
              >
                {words.map((w, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, filter: "blur(4px)" }}
                    animate={{ opacity: 1, filter: "blur(0px)" }}
                    transition={{ duration: 0.5 }}
                    className="inline"
                  >
                    {w}{" "}
                  </motion.span>
                ))}
                {!streamDone && (
                  <motion.span
                    data-testid="stream-cursor"
                    aria-hidden="true"
                    className="ml-1 inline-block h-[0.9em] w-[2px] translate-y-[0.15em] bg-amber-400"
                    animate={{ opacity: [1, 0.15, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  />
                )}
              </p>
              <AnimatePresence>
                {phase === "revealed" && !streamDone && answer && (
                  <motion.p
                    key="forming"
                    data-testid="vision-forming"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="mt-6 text-center text-sm font-medium uppercase tracking-[0.3em] text-white/45"
                  >
                    {t.visionForming}
                  </motion.p>
                )}
              </AnimatePresence>
              {streamDone && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="mt-10 flex flex-wrap items-center justify-center gap-4"
              >
                <button
                  onClick={share}
                  disabled={sharing}
                  data-testid="share-button"
                  className="group flex items-center gap-3 rounded-full bg-amber-500 px-8 py-4 text-base font-semibold uppercase tracking-[0.3em] text-[#030014] shadow-[0_0_40px_rgba(245,158,11,0.3)] transition-[background-color,box-shadow,opacity] duration-400 hover:bg-amber-300 disabled:opacity-70"
                >
                  {sharing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Share2 className="h-5 w-5" />}
                  {sharing ? t.sharePreparing : t.share}
                </button>
                <button
                  onClick={reset}
                  data-testid="terminer-button"
                  className="group flex items-center gap-3 rounded-full border border-amber-500/40 px-8 py-4 text-base font-medium uppercase tracking-[0.3em] text-amber-300 transition-[background-color,border-color,color] duration-400 hover:border-amber-400 hover:bg-amber-500/10"
                >
                  <RotateCcw className="h-5 w-5 transition-transform duration-500 group-hover:-rotate-180" />
                  {t.terminer}
                </button>
              </motion.div>
              )}
              <AnimatePresence>
                {shareMsg && (
                  <motion.p
                    data-testid="share-feedback"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-5 text-center text-base text-amber-300/90"
                  >
                    {shareMsg}
                  </motion.p>
                )}
              </AnimatePresence>
              <div ref={answerEndRef} aria-hidden="true" className="h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default OracleHero;
